import { z } from 'zod';
import { ARTIFACT_MAX_LEVEL } from '../inventory/artifacts';
import { artifactsResponseSchema } from './artifacts';
import { huntSessionStateSchema, progressionSchema } from './hunt';
import { activePetSchema } from './pets';
import { decimalStringSchema, digestSchema } from './primitives';

/**
 * Merging two identical artifact copies (prompt 3D §9).
 *
 * Separate from `artifacts.ts` because the answer carries a hunt session, and
 * the hunt's own contract already reports drops from `artifacts.ts`. Keeping
 * this leaf at the end of the chain is what stops the two from importing each
 * other - which, with zod schemas evaluated at module load, would leave one of
 * them undefined rather than merely untidy.
 *
 * Combining is MANUAL and always will be. Nothing merges duplicates on the
 * player's behalf: a second copy is a thing they may want to sell, and
 * consuming it automatically would spend an item they never offered.
 */

export const ARTIFACT_COMBINE_ROUTE = '/api/artifacts/combine' as const;

/**
 * Two instance ids and a replay guard. That is the entire request.
 *
 * It cannot name a definition, a level or a result: all three are read from
 * the two rows, under their locks, inside the transaction. A client that lies
 * is simply naming items it does not own. `.strict()`, so a request that tries
 * to add `level`, `definitionId` or `toLevel` is rejected rather than ignored.
 */
export const artifactCombineRequestSchema = z
  .object({
    firstInstanceId: z.uuid(),
    secondInstanceId: z.uuid(),
    /** Replay guard for THIS request. Never used as a ledger key. */
    idempotencyKey: z.string().min(8).max(128),
  })
  .strict();
export type ArtifactCombineRequest = z.infer<typeof artifactCombineRequestSchema>;

export const artifactCombineResponseSchema = z.object({
  /** What the merge did, from the server's own record of it. */
  combined: z.object({
    definitionId: z.string().min(1),
    consumedInstanceIds: z.tuple([z.uuid(), z.uuid()]),
    newInstanceId: z.uuid(),
    fromLevel: z.number().int().min(1),
    toLevel: z.number().int().min(2).max(ARTIFACT_MAX_LEVEL),
  }),
  /** The complete artifact state afterwards, re-derived from the vault. */
  artifacts: artifactsResponseSchema,
  /** Unchanged by an artifact merge; stated so the client need not assume it. */
  activePet: activePetSchema.nullable(),
  /**
   * The SAME session, reconfigured in place. The arena is never recreated:
   * mobs, boss, timers, the spawn schedule and the shots in flight all
   * continue, and only volleys fired from now on use the stronger artifact.
   */
  session: huntSessionStateSchema,
  snapshot: z.unknown(),
  snapshotDigest: digestSchema,
  progression: progressionSchema,
  /** What the pending tail earned under the OLD passives, before the merge. */
  settled: z.object({
    validatedMs: z.number().int().min(0),
    xpEarned: decimalStringSchema,
    goldEarned: decimalStringSchema,
    levelsGained: decimalStringSchema,
  }),
  /** True when the same key was already applied; nothing merged again. */
  replayed: z.boolean(),
});
export type ArtifactCombineResponse = z.infer<typeof artifactCombineResponseSchema>;
